import React from "react";
import {
  ArrowRight, BarChart3, CalendarDays, Compass, Gauge, MapPin,
  Sparkles, Target, TrendingDown, TrendingUp, Trophy,
} from "lucide-react";

const KPI_ICONS = {
  volume: BarChart3,
  growth: TrendingUp,
  share: Gauge,
  rank: Trophy,
};

const KPI_ACCENTS = {
  volume: "border-t-blue-400 text-blue-600",
  growth: "border-t-violet-400 text-violet-600",
  share: "border-t-amber-400 text-amber-600",
  rank: "border-t-emerald-400 text-emerald-600",
};

const CONFIDENCE_LABELS = {
  High: "élevée",
  Medium: "moyenne",
  Low: "faible",
};

const SummaryKpi = ({ item }) => {
  const Icon = KPI_ICONS[item?.key] || BarChart3;
  const value = item?.loading ? "…" : item?.value || "—";
  return (
    <article className={`rounded-2xl border border-t-4 border-lodge-hairline bg-white p-5 shadow-lodge-sm ${KPI_ACCENTS[item?.key] || KPI_ACCENTS.volume}`}>
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-current/10"><Icon size={21} /></span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-lodge-ardoise">{item?.label}</p>
          <p className={`mt-0.5 truncate font-heading text-3xl font-bold tracking-tight ${item?.positive === false ? "text-rose-600" : "text-lodge-graphite"}`}>
            {value}{item?.unit ? <span className="ml-1 text-xl">{item.unit}</span> : null}
          </p>
          <p className="mt-1 truncate text-xs text-slate-400">{item?.sub || item?.deltaLabel || "Marché sélectionné"}</p>
        </div>
      </div>
    </article>
  );
};

const decisionCopy = (growth, opportunity) => {
  if (opportunity?.type === "decline" || Number(growth) < 0) {
    return {
      title: "Reconquérir la demande est votre priorité.",
      detail: "Identifiez les segments à réactiver et préparez une action ciblée.",
      tone: "text-rose-600",
      Icon: TrendingDown,
    };
  }
  if (opportunity?.type === "growth" || Number(growth) > 0) {
    return {
      title: "Accélérer la dynamique de ce marché.",
      detail: "Transformez la progression observée en demande additionnelle.",
      tone: "text-emerald-600",
      Icon: TrendingUp,
    };
  }
  return {
    title: opportunity?.opportunity || "Le prochain levier apparaîtra avec les données disponibles.",
    detail: "Consolidez les signaux tourisme, aérien et marché avant d’agir.",
    tone: "text-blue-600",
    Icon: Target,
  };
};

export default function MarketDecisionOverview({
  marketLabel,
  destinationName,
  period,
  snapshotKpis,
  opportunity,
  sourceLabel,
  immutableDemo,
  onExplore,
  onRunAction,
  onAnalyze,
}) {
  const growth = snapshotKpis?.growth?.value;
  const lead = decisionCopy(growth, opportunity);
  const LeadIcon = lead.Icon;
  const confidence = CONFIDENCE_LABELS[opportunity?.confidence] || "à confirmer";
  const canRunAction = opportunity && !(immutableDemo && opportunity.primaryAction === "campaign");
  const scrollToDetails = () => document.getElementById("lodge-opportunities")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <section className="space-y-5" data-testid="market-decision-overview">
      <div className="flex flex-col gap-3 rounded-2xl border border-sky-100 bg-gradient-to-r from-sky-50 via-white to-emerald-50 px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-lodge-deep">Vue marché</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-lodge-graphite sm:text-4xl" data-testid="market-overview-title">{marketLabel || "Marché sélectionné"}</h1>
          <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-lodge-ardoise"><MapPin size={14} /> Marché émetteur vers {destinationName || "la destination"}</p>
        </div>
        <div className="inline-flex items-center gap-2 self-start rounded-xl border border-white bg-white/80 px-3 py-2 text-xs font-semibold text-lodge-deep shadow-sm">
          <CalendarDays size={15} /> {period || "Période indisponible"}
        </div>
      </div>

      <div className="flex flex-col gap-4 overflow-hidden rounded-2xl border border-emerald-100 bg-gradient-to-r from-emerald-50 to-teal-50 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="market-essential">
        <div className="flex items-start gap-4">
          <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white shadow-sm ${lead.tone}`}><LeadIcon size={22} /></span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-lodge-deep">L’essentiel aujourd’hui</p>
            <p className="mt-0.5 font-heading text-xl font-bold text-lodge-graphite">{lead.title}</p>
            <p className="mt-1 text-sm text-lodge-ardoise">{opportunity?.opportunity || lead.detail}</p>
          </div>
        </div>
        <button type="button" onClick={scrollToDetails} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-lodge-deep px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#2C5540]">
          Voir les leviers <ArrowRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="market-primary-kpis">
        {["volume", "growth", "share", "rank"].map((key) => <SummaryKpi key={key} item={snapshotKpis?.[key] || { key }} />)}
      </div>

      <article className="rounded-2xl border border-lodge-hairline bg-white p-5 shadow-lodge-sm" data-testid="market-priority-action">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-violet-50 text-violet-600"><Sparkles size={20} /></span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-lodge-deep">Décision recommandée</p>
                <span className="rounded-full bg-lodge-mint/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-lodge-deep">{opportunity?.source || sourceLabel || "Données disponibles"}</span>
              </div>
              <h2 className="mt-1 font-heading text-xl font-bold text-lodge-graphite">{opportunity?.signal || "Analyse du marché en cours"}</h2>
              <p className="mt-1 text-sm text-lodge-ardoise">{opportunity?.evidence || "Aucune recommandation déterministe n’est disponible avec les données actuelles."}</p>
              <p className="mt-2 text-xs text-slate-400">Confiance {confidence}{opportunity?.recommendedAction ? ` · ${opportunity.recommendedAction}` : ""}</p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {opportunity && onAnalyze && <button type="button" onClick={() => onAnalyze(opportunity)} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-lodge-deep hover:border-lodge-deep"><Sparkles size={14} /> Analyser</button>}
            <button type="button" onClick={() => opportunity && onExplore(opportunity)} disabled={!opportunity} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-lodge-deep hover:border-lodge-deep disabled:cursor-not-allowed disabled:opacity-50"><Compass size={14} /> Explorer</button>
            {opportunity && <button type="button" onClick={() => canRunAction && onRunAction(opportunity)} disabled={!canRunAction} className="inline-flex items-center gap-1.5 rounded-xl bg-lodge-deep px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2C5540] disabled:cursor-not-allowed disabled:bg-slate-300">Préparer l’action <ArrowRight size={14} /></button>}
          </div>
        </div>
        {immutableDemo && <p className="mt-4 text-[11px] text-slate-400">Données de démonstration · recommandations à valider · aucune campagne n’est lancée depuis cet écran.</p>}
      </article>
    </section>
  );
}
